import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Sparkles, ChefHat, Users, Repeat, Star } from "lucide-react";
import { useSiteData, useFetch, waLink } from "../lib/hooks";
import { fileUrl } from "../lib/api";
import { Reveal, Overline } from "../components/site/Reveal";
import FlavourCollection from "../components/site/FlavourCollection";
import { DEFAULT_IMAGES } from "../lib/defaults";

const IMG = (s, k, fb = "") => fileUrl(s?.images?.[k] || DEFAULT_IMAGES[k] || fb);

export default function Home() {
  const s = useSiteData();
  const { data: menu } = useFetch("/public/menu");
  const { data: gallery } = useFetch("/public/gallery");
  const { data: testimonials } = useFetch("/public/testimonials");
  const flavourItems = (s.flavour_mode === "GALLERY"
    ? [...new Map(gallery.map((g) => [g.category, g])).values()].map((g) => ({ id: g.id, name: g.category, image: g.image, category: g.category }))
    : menu.map((c) => ({ id: c.id, name: c.name.replace("THE ", ""), image: c.image }))
  );

  return (
    <div>
      <Hero s={s} />
      <BrandIntro s={s} />
      <ExperienceSection s={s} />
      <Signature s={s} />
      {flavourItems.length > 0 && <FlavourCollection items={flavourItems} mode={s.flavour_mode || "MENU"} />}
      <CulinaryPreview menu={menu} />
      <EventsSection s={s} />
      <WeddingFeature s={s} />
      <CorporateSection s={s} />
      <LiveFood s={s} />
      <WhyUs />
      <Approach />
      <GalleryPreview gallery={gallery} />
      <Testimonials items={testimonials} />
      <Experience3DBanner s={s} />
      <FinalCTA s={s} />
    </div>
  );
}

/* ---------------- HERO ---------------- */
function Hero({ s }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ scale }} className="absolute inset-0">
        <img src={fileUrl(s.hero_image || IMG(s, "hero"))} alt="Premium Indian catering buffet" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-velvet via-burgundy/55 to-burgundy/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-velvet/70 to-transparent" />
      <motion.div style={{ y }} className="relative h-full max-w-[1400px] mx-auto px-4 sm:px-8 flex flex-col justify-center">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-gold" />
            <span className="overline !text-gold">Foodie Connect · Caterer</span>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <span className="overline !text-ivory/60 !tracking-[0.4em] mb-3 block">Offering Flavours</span>
        </Reveal>
        <Reveal delay={0.2}>
          <h1 className="font-serif text-ivory text-shadow-lg text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-7xl xl:text-8xl max-w-4xl">
            Where great food<br />meets <span className="italic gold-text">great occasions.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.35}>
          <p className="text-ivory/80 text-base sm:text-lg max-w-xl mt-6 font-light leading-relaxed">
            {s.hero_sub || "Premium catering crafted for celebrations, businesses and every occasion worth gathering for."}
          </p>
        </Reveal>
        <Reveal delay={0.5}>
          <div className="flex flex-wrap gap-4 mt-9">
            <Link to="/quote-builder" className="btn-gold" data-testid="hero-plan-event"><Sparkles className="w-4 h-4" /> Plan Your Event</Link>
            <Link to="/menus" className="btn-outline-gold" data-testid="hero-explore-menus">Explore Our Menus</Link>
          </div>
        </Reveal>
      </motion.div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <div className="scroll-dot" />
        <span className="text-ivory/40 text-[0.6rem] tracking-[0.3em]">SCROLL</span>
      </div>
    </section>
  );
}

/* ---------------- BRAND INTRO ---------------- */
function BrandIntro({ s }) {
  return (
    <section className="relative py-24 sm:py-32 bg-ivory overflow-hidden" data-testid="brand-intro">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Overline>The Heart of Every Gathering</Overline>
          <h2 className="font-serif text-burgundy text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 mb-7">
            Food is the centre of every gathering.
          </h2>
          <p className="text-charcoal/75 leading-relaxed text-base sm:text-lg mb-5">
            At Foodie Connect Caterer, we bring together thoughtfully crafted food, professional hospitality
            and seamless execution to create experiences people remember.
          </p>
          <p className="text-charcoal/60 leading-relaxed mb-8">
            From intimate private dinners to grand weddings and large-scale corporate programmes, we cater
            weddings, celebrations, private gatherings, corporate events, conferences, bulk food requirements,
            recurring meal services and institutional needs — all with the same devotion to flavour.
          </p>
          <div className="grid grid-cols-3 gap-4 max-w-md">
            {[["500+", "Events Catered"], ["12+", "Signature Menus"], ["1M+", "Meals Served"]].map(([a, b]) => (
              <div key={b}>
                <div className="font-serif text-3xl sm:text-4xl gold-text">{a}</div>
                <div className="text-xs text-charcoal/55 mt-1 tracking-wide">{b}</div>
              </div>
            ))}
          </div>
        </Reveal>

        <div className="relative h-[520px] sm:h-[600px]">
          <Reveal delay={0.1} className="absolute left-0 top-0 w-[58%] h-[85%]">
            <div className="w-full h-full frame-arch border border-gold/30 shadow-2xl group overflow-hidden">
              <img src={IMG(s, "grill_v")} alt="Chef grilling kebabs" className="w-full h-full object-cover img-zoom" />
            </div>
          </Reveal>
          <Reveal delay={0.3} className="absolute right-0 bottom-0 w-[46%] aspect-square">
            <div className="w-full h-full frame-oval border-2 border-gold/40 shadow-2xl group overflow-hidden bg-cream p-1">
              <img src={IMG(s, "biryani")} alt="Dum biryani" className="w-full h-full object-cover rounded-[50%] img-zoom" />
            </div>
          </Reveal>
          <Reveal delay={0.5} className="absolute right-4 top-4 w-28 h-28 sm:w-36 sm:h-36">
            <div className="w-full h-full rounded-full border border-gold/40 overflow-hidden shadow-xl glass p-1 float-slow">
              <img src={IMG(s, "prawns")} alt="Prawns" className="w-full h-full object-cover rounded-full" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- EXPERIENCE ---------------- */
const EXPERIENCES = [
  { n: "01", t: "Celebrations", d: "Weddings, receptions, anniversaries, birthdays and private gatherings.", k: "wedding" },
  { n: "02", t: "Corporate", d: "Conferences, meetings, office events, launches and corporate dining.", k: "corporate" },
  { n: "03", t: "Everyday Food", d: "Recurring meal programs, employee meals, institutional food and daily requirements.", k: "corp_boxes" },
  { n: "04", t: "Special Events", d: "Large gatherings, social events, live counters, themed food experiences and custom menus.", k: "flame" },
];
function ExperienceSection({ s }) {
  return (
    <section className="relative py-24 sm:py-32 bg-espresso grain text-ivory overflow-hidden" data-testid="experience-section">
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8">
        <Reveal><Overline>What We Do</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 mb-14 max-w-3xl">The Foodie Connect <span className="italic gold-text">Experience</span></h2>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2">
          {EXPERIENCES.map((e, i) => (
            <Reveal key={e.n} delay={i * 0.08}>
              <div className={`group relative overflow-hidden rounded-2xl border border-gold/15 ${i % 2 ? "md:mt-12" : ""}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={IMG(s, e.k)} alt={e.t} className="w-full h-full object-cover img-zoom brightness-[0.7]" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-velvet via-velvet/30 to-transparent" />
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <span className="font-serif text-6xl sm:text-7xl text-gold/30 leading-none absolute top-5 right-6">{e.n}</span>
                  <h3 className="font-serif text-3xl sm:text-4xl mb-2">{e.t}</h3>
                  <p className="text-ivory/70 text-sm max-w-md">{e.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- SIGNATURE ---------------- */
function Signature({ s }) {
  const seq = [["kebab", "tandoori"], ["biryani", "biryani2"], ["seafood", "prawns"], ["curry", "mutton"], ["grill", "flame"], ["dessert", "gulab"]];
  const labels = ["Kebabs", "Biryani", "Seafood", "Curries", "Grills", "Desserts"];
  return (
    <section className="relative py-24 sm:py-32 bg-ivory" data-testid="signature-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
        <Reveal className="text-center max-w-3xl mx-auto mb-14">
          <Overline className="justify-center">Signature Culinary Experience</Overline>
          <h2 className="font-serif text-burgundy text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
            Crafted for the table.<br /><span className="italic gold-text">Remembered after the last bite.</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          {seq.map(([label, k], i) => (
            <Reveal key={k} delay={(i % 3) * 0.08}>
              <div className="group relative overflow-hidden rounded-2xl aspect-[4/5]">
                <img src={IMG(s, k)} alt={labels[i]} className="w-full h-full object-cover img-zoom" />
                <div className="absolute inset-0 bg-gradient-to-t from-velvet/85 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5">
                  <span className="overline !text-gold/80">0{i + 1}</span>
                  <h3 className="font-serif text-ivory text-2xl sm:text-3xl">{labels[i]}</h3>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/menus" className="btn-burgundy" data-testid="signature-discover">Discover Our Menus <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </section>
  );
}

/* ---------------- CULINARY PREVIEW ---------------- */
function CulinaryPreview({ menu }) {
  const shapeCls = { arch: "frame-arch", oval: "rounded-[46%]", portrait: "rounded-3xl", pill: "frame-pill" };
  return (
    <section className="relative py-24 sm:py-32 bg-cream" data-testid="culinary-collection">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
        <Reveal className="mb-14">
          <Overline>The Culinary Collection</Overline>
          <div className="flex flex-wrap items-end justify-between gap-6 mt-4">
            <h2 className="font-serif text-burgundy text-4xl sm:text-5xl lg:text-6xl max-w-2xl leading-[1.05]">Explore the culinary collection</h2>
            <Link to="/menus" className="text-gold hover:text-burgundy transition-colors flex items-center gap-2 text-sm tracking-widest uppercase">View All <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
          {menu.map((c, i) => (
            <Reveal key={c.id} delay={(i % 6) * 0.06}>
              <Link to={`/menus?c=${c.id}`} data-testid={`culinary-cat-${i}`} className={`group block ${i % 2 ? "md:mt-8" : ""}`}>
                <div className={`relative overflow-hidden border border-gold/30 shadow-lg aspect-[3/4] ${shapeCls[c.shape] || "rounded-3xl"} bg-white`}>
                  <img src={fileUrl(c.image)} alt={c.name} className="w-full h-full object-cover img-zoom" />
                  <div className="absolute inset-0 bg-gradient-to-t from-velvet/80 to-transparent" />
                  <div className="absolute bottom-4 left-0 right-0 px-3 text-center">
                    <h3 className="font-serif text-ivory text-lg leading-tight">{c.name}</h3>
                  </div>
                </div>
                <p className="text-center text-xs text-charcoal/55 mt-3 italic">{c.tagline}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- EVENTS ---------------- */
function EventsSection({ s }) {
  const events = ["Weddings", "Receptions", "Birthdays", "Anniversaries", "Engagements", "Kitty Parties", "Private Events", "Corporate Events", "Conferences", "Product Launches", "Social Gatherings"];
  return (
    <section className="relative py-24 sm:py-32 bg-ivory overflow-hidden" data-testid="events-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>Every Occasion</Overline>
            <h2 className="font-serif text-burgundy text-4xl sm:text-5xl lg:text-6xl mt-5 mb-7 leading-[1.05]">Every occasion has a <span className="italic gold-text">flavour.</span></h2>
            <div className="flex flex-wrap gap-2.5">
              {events.map((e) => (
                <span key={e} className="px-4 py-2 rounded-full border border-gold/30 text-sm text-charcoal/70 hover:bg-burgundy hover:text-ivory hover:border-burgundy transition-colors">{e}</span>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 gap-4 h-[520px]">
          <Reveal className="row-span-2"><div className="group h-full overflow-hidden rounded-3xl"><img src={IMG(s, "wedding")} alt="Wedding" className="w-full h-full object-cover img-zoom" /></div></Reveal>
          <Reveal delay={0.15}><div className="group h-full overflow-hidden rounded-3xl"><img src={IMG(s, "toast")} alt="Celebration" className="w-full h-full object-cover img-zoom" /></div></Reveal>
          <Reveal delay={0.3}><div className="group h-full overflow-hidden rounded-3xl"><img src={IMG(s, "corporate")} alt="Corporate" className="w-full h-full object-cover img-zoom" /></div></Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- WEDDING ---------------- */
function WeddingFeature({ s }) {
  return (
    <section className="relative min-h-[80vh] flex items-center overflow-hidden" data-testid="wedding-feature">
      <img src={IMG(s, "wedding2")} alt="Wedding catering" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-velvet/90 via-burgundy/70 to-transparent" />
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8 py-24 w-full">
        <Reveal className="max-w-xl">
          <Overline>The Wedding Table</Overline>
          <h2 className="font-serif text-ivory text-4xl sm:text-6xl lg:text-7xl leading-[1.02] mt-5 mb-6">
            Your day.<br />Your people.<br /><span className="italic gold-text">Your table.</span>
          </h2>
          <p className="text-ivory/75 leading-relaxed mb-6 max-w-md">
            Custom menus, buffet planning, live counters, professional service and guest-scale catering —
            designed around the celebration of a lifetime.
          </p>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-2 text-ivory/70 text-sm mb-9 max-w-md">
            {["Custom Menus", "Buffet Planning", "Live Counters", "Professional Service", "Food Presentation", "Guest-Scale Catering"].map((x) => (
              <li key={x} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gold" />{x}</li>
            ))}
          </ul>
          <Link to="/quote-builder" className="btn-gold" data-testid="wedding-plan">Plan Your Wedding <ArrowRight className="w-4 h-4" /></Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- CORPORATE ---------------- */
function CorporateSection({ s }) {
  const items = ["Office meals", "Employee meals", "Corporate lunches", "Meetings", "Conferences", "Training programs", "Institutional meals", "Recurring meal plans", "Bulk food requirements"];
  return (
    <section className="relative py-24 sm:py-32 bg-charcoal text-ivory" data-testid="corporate-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal className="order-2 lg:order-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="group overflow-hidden rounded-2xl aspect-[3/4]"><img src={IMG(s, "corporate")} alt="Corporate catering" className="w-full h-full object-cover img-zoom" /></div>
            <div className="group overflow-hidden rounded-2xl aspect-[3/4] mt-8"><img src={IMG(s, "corp_boxes")} alt="Boxed meals" className="w-full h-full object-cover img-zoom" /></div>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="order-1 lg:order-2">
          <Overline>Corporate &amp; Daily Food Services</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 mb-6 leading-[1.05]">Food that works around <span className="italic gold-text">your business.</span></h2>
          <p className="text-ivory/65 leading-relaxed mb-7 max-w-md">
            Reliable, scalable and consistent food programmes — from a single conference to daily meals for
            hundreds. Professional logistics, dependable timing, refined quality.
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm text-ivory/70 mb-9 max-w-md">
            {items.map((x) => <div key={x} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gold" />{x}</div>)}
          </div>
          <Link to="/corporate" className="btn-outline-gold" data-testid="corporate-discuss">Discuss Your Requirement <ArrowRight className="w-4 h-4" /></Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- LIVE FOOD ---------------- */
function LiveFood({ s }) {
  const counters = [
    { t: "Live Tandoor", k: "grill_v" }, { t: "Kebab Station", k: "tandoori" }, { t: "Biryani Station", k: "biryani2" },
    { t: "Grill Counter", k: "flame" }, { t: "Seafood Bar", k: "prawns" }, { t: "Dessert Station", k: "sweets" },
  ];
  return (
    <section className="relative py-24 sm:py-32 bg-velvet grain text-ivory overflow-hidden" data-testid="live-food">
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8">
        <Reveal className="text-center max-w-2xl mx-auto mb-14">
          <Overline className="justify-center">Live Food Experiences</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5">See the flavour <span className="italic gold-text">come alive.</span></h2>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {counters.map((c, i) => (
            <Reveal key={c.t} delay={(i % 3) * 0.08}>
              <div className="group relative overflow-hidden rounded-2xl aspect-square border border-gold/15">
                <img src={IMG(s, c.k)} alt={c.t} className="w-full h-full object-cover img-zoom brightness-[0.75] group-hover:brightness-100 transition-all" />
                <div className="absolute inset-0 bg-gradient-to-t from-velvet/80 to-transparent" />
                <div className="absolute bottom-5 left-5 flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full border border-gold/50 flex items-center justify-center"><ChefHat className="w-4 h-4 text-gold" /></span>
                  <h3 className="font-serif text-2xl">{c.t}</h3>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- WHY US ---------------- */
function WhyUs() {
  const points = [
    { i: ChefHat, t: "Culinary Craft", d: "Thoughtfully designed menus and preparation." },
    { i: Repeat, t: "Flexible Menus", d: "Menus adapted to the occasion and requirements." },
    { i: Users, t: "Professional Service", d: "Well-coordinated catering execution." },
    { i: Sparkles, t: "Scalable Operations", d: "Suitable for small gatherings and large requirements." },
    { i: Star, t: "Food Safety & Hygiene", d: "Professional food handling practices." },
    { i: ArrowRight, t: "Seamless Execution", d: "Planning, preparation, logistics and service." },
  ];
  return (
    <section className="relative py-24 sm:py-32 bg-ivory" data-testid="why-us">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
        <Reveal className="text-center mb-14">
          <Overline className="justify-center">Why Foodie Connect</Overline>
          <h2 className="font-serif text-burgundy text-4xl sm:text-5xl lg:text-6xl mt-5">Details make the <span className="italic gold-text">difference.</span></h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gold/20 rounded-2xl overflow-hidden border border-gold/20">
          {points.map((p, i) => (
            <Reveal key={p.t} delay={(i % 3) * 0.08}>
              <div className="bg-ivory p-8 sm:p-10 h-full hover:bg-cream transition-colors group">
                <span className="inline-flex w-12 h-12 rounded-full border border-gold/40 items-center justify-center mb-5 group-hover:bg-burgundy transition-colors">
                  <p.i className="w-5 h-5 text-gold" />
                </span>
                <h3 className="font-serif text-2xl text-burgundy mb-2">{p.t}</h3>
                <p className="text-charcoal/60 text-sm leading-relaxed">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- APPROACH ---------------- */
function Approach() {
  const steps = [
    ["01", "Understand", "Understand the occasion, guests and requirements."],
    ["02", "Design", "Build the menu and service experience."],
    ["03", "Prepare", "Prepare food with attention to quality and consistency."],
    ["04", "Deliver", "Execute the catering experience professionally."],
    ["05", "Delight", "Leave guests remembering the food."],
  ];
  return (
    <section className="relative py-24 sm:py-32 bg-burgundy grain text-ivory overflow-hidden" data-testid="approach-section">
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8">
        <Reveal className="mb-14"><Overline>Our Approach</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4">From first call to <span className="italic gold-text">last bite.</span></h2>
        </Reveal>
        <div className="grid md:grid-cols-5 gap-8 relative">
          <div className="hidden md:block absolute top-6 left-0 right-0 h-px bg-gold/25" />
          {steps.map((st, i) => (
            <Reveal key={st[0]} delay={i * 0.1}>
              <div className="relative">
                <div className="w-12 h-12 rounded-full bg-gold text-velvet font-serif text-xl flex items-center justify-center mb-5 relative z-10">{st[0]}</div>
                <h3 className="font-serif text-2xl mb-2">{st[1]}</h3>
                <p className="text-ivory/60 text-sm leading-relaxed">{st[2]}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- GALLERY PREVIEW ---------------- */
function GalleryPreview({ gallery }) {
  const items = gallery.slice(0, 7);
  if (!items.length) return null;
  return (
    <section className="relative py-24 sm:py-32 bg-ivory" data-testid="gallery-preview">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div><Overline>The Gallery</Overline><h2 className="font-serif text-burgundy text-4xl sm:text-5xl lg:text-6xl mt-4">Moments, plated.</h2></div>
          <Link to="/gallery" className="btn-burgundy" data-testid="gallery-viewall">View Full Gallery <ArrowRight className="w-4 h-4" /></Link>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 auto-rows-[180px] sm:auto-rows-[220px]">
          {items.map((g, i) => (
            <Reveal key={g.id} delay={(i % 4) * 0.06} className={`${i === 0 ? "col-span-2 row-span-2" : ""} ${i === 3 ? "row-span-2" : ""}`}>
              <Link to="/gallery" className="group block w-full h-full overflow-hidden rounded-2xl">
                <img src={fileUrl(g.image)} alt={g.name} className="w-full h-full object-cover img-zoom" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- TESTIMONIALS ---------------- */
function Testimonials({ items }) {
  if (!items.length) return null;
  return (
    <section className="relative py-24 sm:py-32 bg-cream" data-testid="testimonials-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
        <Reveal className="text-center mb-14"><Overline className="justify-center">Kind Words</Overline>
          <h2 className="font-serif text-burgundy text-4xl sm:text-5xl lg:text-6xl mt-5">What our guests say.</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.1}>
              <div className="bg-ivory border border-gold/20 rounded-2xl p-8 h-full flex flex-col">
                <div className="flex gap-1 mb-4">{Array.from({ length: t.rating || 5 }).map((_, j) => <Star key={j} className="w-4 h-4 fill-gold text-gold" />)}</div>
                <p className="font-serif text-xl text-charcoal/85 italic leading-relaxed flex-1">"{t.quote}"</p>
                <div className="mt-6 pt-5 border-t border-gold/15">
                  <p className="font-semibold text-burgundy">{t.name}</p>
                  <p className="text-xs text-charcoal/50 tracking-wide">{t.role}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- 3D BANNER ---------------- */
function Experience3DBanner({ s }) {
  if (s.show_3d === false) return null;
  return (
    <section className="relative py-20 overflow-hidden bg-velvet" data-testid="experience-3d-banner">
      <img src={IMG(s, "chef")} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
        <div>
          <Overline className="justify-center md:justify-start">Optional Immersive Experience</Overline>
          <h2 className="font-serif text-ivory text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-2xl">Step inside our virtual catering venue.</h2>
          <p className="text-ivory/60 mt-3 max-w-xl">Walk through a premium dining hall, live counters and buffets — meet our virtual reception desk.</p>
        </div>
        <Link to="/experience" className="btn-gold shrink-0" data-testid="enter-3d-experience"><Sparkles className="w-4 h-4" /> Enter the 3D Experience</Link>
      </div>
    </section>
  );
}

/* ---------------- FINAL CTA ---------------- */
function FinalCTA({ s }) {
  return (
    <section className="relative py-28 sm:py-36 bg-burgundy grain overflow-hidden" data-testid="final-cta">
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8 text-center">
        <Reveal>
          <Overline className="justify-center">Let's Begin</Overline>
          <h2 className="font-serif text-ivory text-4xl sm:text-6xl lg:text-7xl mt-6 leading-[1.03] max-w-4xl mx-auto">
            Let's create something<br /><span className="italic gold-text">worth gathering for.</span>
          </h2>
          <p className="text-ivory/70 max-w-xl mx-auto mt-6 leading-relaxed">
            Tell us about your occasion and let our catering team build the experience around it.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <Link to="/quote-builder" className="btn-gold" data-testid="final-plan">Plan Your Event</Link>
            <a href={waLink(s)} target="_blank" rel="noreferrer" className="btn-outline-gold" data-testid="final-whatsapp"><i className="fa-brands fa-whatsapp" /> WhatsApp Us</a>
            <Link to="/contact" className="btn-outline-gold" data-testid="final-quote">Request a Quote</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
